// fetchDeadline: a fetch that can never wait forever.
//
// A request that never answers is worse than one that fails. A failure tells the
// shell what to draw; a stall leaves a folder dimmed, or its ghost tiles up, for
// as long as the tab lives, and holds the in-flight slot so every later open of
// that project rides the same dead request. So the shell's own reads all go
// through here: the server gets SHELL_FETCH_MS to START answering, and once it
// has, the body gets BODY_CEILING_MS to arrive (a big subtree on a slow line is
// slow, not stuck, and must not be cut off at the first number).
//
// The deadline is enforced twice, on purpose. The abort signal ends a real
// request and rejects a body that was mid-read; the race ends the PROMISE even
// when whatever answers the fetch ignores the signal (a service worker, an
// in-browser stand-in), so the caller's `await` is bounded either way.
//
// For request/response calls. Not for streams: the body ceiling would cut one.

/** how long the server gets to start answering. */
export const SHELL_FETCH_MS = 8_000;
/** how long the body gets once it has. */
export const BODY_CEILING_MS = 30_000;

export type DeadlineInit = RequestInit & {
  /** time to first byte; default SHELL_FETCH_MS. Infinity turns the deadline off. */
  timeoutMs?: number;
  /** time for the body after the answer began; default BODY_CEILING_MS. */
  bodyMs?: number;
};

/** the server did not answer in time. Not an outage by itself: the caller decides. */
export class FetchTimeoutError extends Error {
  readonly timeoutMs: number;
  constructor(url: string, timeoutMs: number) {
    super(`no answer from ${url} in ${timeoutMs}ms`);
    this.name = "FetchTimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

export function isTimeout(err: unknown): err is FetchTimeoutError {
  return err instanceof Error && err.name === "FetchTimeoutError";
}

function abortReason(signal?: AbortSignal | null): unknown {
  return signal?.reason ?? new DOMException("the request was cancelled", "AbortError");
}

export function fetchWithDeadline(url: string, opts: DeadlineInit = {}): Promise<Response> {
  const { timeoutMs = SHELL_FETCH_MS, bodyMs = BODY_CEILING_MS, signal: outer, ...init } = opts;
  const ctl = new AbortController();

  return new Promise<Response>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      outer?.removeEventListener("abort", onOuter);
    };
    const onOuter = () => {
      finish();
      if (!settled) {
        settled = true;
        reject(abortReason(outer));
      }
      ctl.abort(abortReason(outer));
    };
    const arm = (ms: number, then: () => void) => {
      if (timer) clearTimeout(timer);
      // setTimeout(Infinity) fires at once in browsers: "no deadline" must mean none
      timer = Number.isFinite(ms) ? setTimeout(then, ms) : null;
    };

    if (outer?.aborted) {
      reject(abortReason(outer));
      return;
    }
    outer?.addEventListener("abort", onOuter, { once: true });

    arm(timeoutMs, () => {
      timer = null;
      const err = new FetchTimeoutError(url, timeoutMs);
      if (!settled) {
        settled = true;
        reject(err);
      }
      outer?.removeEventListener("abort", onOuter);
      ctl.abort(err);
    });

    let pending: Promise<Response>;
    try {
      pending = fetch(url, { ...init, signal: ctl.signal });
    } catch (e) {
      finish();
      settled = true;
      reject(e);
      return;
    }
    pending.then(
      (res) => {
        if (settled) return; // lost the race: the abort already spoke for this request
        settled = true;
        // answered: the first deadline is met. The body is on its own, longer clock.
        arm(bodyMs, () => {
          timer = null;
          outer?.removeEventListener("abort", onOuter);
          ctl.abort(new FetchTimeoutError(url, bodyMs));
        });
        resolve(res);
      },
      (e) => {
        finish();
        if (settled) return;
        settled = true;
        reject(e);
      },
    );
  });
}

/** Run `p`, but give up on waiting after `ms`: resolves `fallback` instead. The
 *  work itself is not cancelled; this only frees whoever is waiting on it (a
 *  loading flag that must come down, a ghost that must not outstay). */
export function within<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(fallback);
      },
    );
  });
}
